<template>
  <div id="app">
    <div class="container">
      <!--UPLOAD-->
      <form
        v-if="isInitial || isSaving"
        enctype="multipart/form-data"
        novalidate
      >
        <div class="dropbox">
          <input
            type="file"
            multiple
            :name="uploadFieldName"
            :disabled="isSaving"
            accept="application/pdf"
            class="input-file"
            @change="filesChange($event.target.name,
                                 $event.target.files);
                     fileCount = $event.target.files.length"
          >
          <p v-if="isInitial">
            Drag your file(s) here to begin<br> or click to browse
            <br> <br>
            PDFs Only
          </p>
          <p
            v-if="isSaving"
            class="mx-3"
          >
            Uploading file... {{ loadPercentage }}%
            <v-progress-linear
              :value="loadPercentage"
            />
          </p>
        </div>
      </form>
      <!--SUCCESS-->
      <div v-if="isSuccess">
        <h2>Loaded file successfully.</h2>
        <p>
          <a
            href="javascript:void(0)"
            @click="reset()"
          >Upload again</a>
        </p>
      </div>
      <!--FAILED-->
      <div v-if="isFailed">
        <h2 :style="{color: $vuetify.theme.themes.light.error, opacity:0.9}">
          Upload failed.
        </h2>
        <p>
          Either the file you uploaded is not in pdf format or it is corrupted.
          <a
            href="javascript:void(0)"
            @click="reset()"
          >Try again</a>
        </p>
        <pre>{{ uploadError }}</pre>
      </div>
    </div>
    <VMessages
      :value="errorBucket"
      color="error"
    />
  </div>
</template>

<script>
import SparkMD5 from 'spark-md5';
import VInput from 'vuetify/lib/components/VInput/VInput';
import { chunkedUploadStart, chunkedUpload, chunkedUploadComplete } from '../services/uploads';

const STATUS_INITIAL = 0;
const STATUS_SAVING = 1;
const STATUS_SUCCESS = 2;
const STATUS_FAILED = 3;

export default {
  name: 'FileUploader',
  extends: VInput,
  props: {
    value: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      uploadError: null,
      currentStatus: null,
      uploadFieldName: 'photos',
      loadPercentage: 0,
    };
  },
  computed: {
    isInitial() {
      return this.currentStatus === STATUS_INITIAL;
    },
    isSaving() {
      return this.currentStatus === STATUS_SAVING;
    },
    isSuccess() {
      return this.currentStatus === STATUS_SUCCESS;
    },
    isFailed() {
      return this.currentStatus === STATUS_FAILED;
    },
  },
  mounted() {
    this.reset();
  },
  methods: {
    reset() {
      // reset form to initial state
      this.currentStatus = STATUS_INITIAL;
      this.$emit('input', null);
      this.uploadError = null;
    },
    async filesChange(fieldName, fileList) {
      this.currentStatus = STATUS_SAVING;
      // handle file changes
      if (!fileList.length) return;
      // append the files to FormData
      const file = fileList?.[0];
      this.loadPercentage = 0;
      try {
        const resultId = await this.chunkedUpload(file);
        this.$emit('input', resultId);
      } catch (e) {
        this.currentStatus = STATUS_FAILED;
        return;
      }
      // save it
      this.currentStatus = STATUS_SUCCESS;
    },
    async chunkedUpload(file) {
      const promise = () => new Promise((resolve, reject) => {
        const fileSize = file.size;
        const chunkSize = 16 * 1024; // bytes
        let offset = 0;
        let chunkReaderBlock = null;
        let uploadId = null;
        const spark = new SparkMD5.ArrayBuffer();
        const readEventHandler = async (evt) => {
          if (evt.target.error == null) {
          // now take the chunks and send them along
            const formData = new FormData();
            spark.append(evt.target.result);
            const chunk = new File([evt.target.result], 'chunk.pdf');
            formData.append('file', chunk);
            if (uploadId) {
              formData.append('upload_id', uploadId);
              const range = { start: offset, end: chunk.size + offset - 1, total: fileSize };
              const { data } = await chunkedUpload(formData, range);
              uploadId = data.upload_id;
              offset = data.offset;
            } else {
              const { data } = await chunkedUploadStart(formData);
              uploadId = data.upload_id;
              offset = data.offset;
            }
            this.loadPercentage = Math.floor((offset / fileSize) * 100);
          } else {
            console.log(`Read error: ${evt.target.error}`);
            return;
          }
          if (offset >= fileSize) {
            console.log('Done reading file');
            const formData = new FormData();
            formData.append('upload_id', uploadId);
            const md5 = spark.end();
            formData.append('md5', md5);
            try {
              await chunkedUploadComplete(formData);
            } catch (e) {
              this.currentStatus = STATUS_FAILED;
              return;
            }
            resolve(uploadId);
            return;
          }

          chunkReaderBlock(offset, chunkSize, file);
        };
        chunkReaderBlock = (_offset, length, _file) => {
          const r = new FileReader();
          const blob = _file.slice(_offset, length + _offset);
          r.onload = readEventHandler;
          r.readAsArrayBuffer(blob);
        // now let's start the read with the first block
        };
        chunkReaderBlock(offset, chunkSize, file);
        return uploadId;
      });
      return promise();
    },
  },
};
</script>

<style lang="scss">
  .dropbox {
    outline: 2px dashed grey; /* the dash box */
    outline-offset: -10px;
    background: white;
    color: dimgray;
    padding: 10px 10px;
    min-height: 100px; /* minimum height */
    position: relative;
    cursor: pointer;
    transition: 0.3s;
  }

  .input-file {
    opacity: 0; /* invisible but it's there! */
    width: 100%;
    height: 150px;
    position: absolute;
    cursor: pointer;
  }

  .dropbox:hover {
    background: lightgray; /* when mouse over to the drop zone, change color */
    transition: 0.3s;
  }

  .dropbox p {
    font-size: 1.2em;
    text-align: center;
    padding: 15px 0 10px 0;
  }
</style>
